import React, { useState, useEffect } from "react";

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  showBootSequence?: boolean;
}

// ASCII: ZERO POINT / DOMAINS (large block style, DOMAINS on separate line)
const ASCII_LOGO = `███████╗███████╗██████╗  ██████╗     ██████╗  ██████╗ ██╗███╗   ██╗████████╗
╚══███╔╝██╔════╝██╔══██╗██╔═══██╗    ██╔══██╗██╔═══██╗██║████╗  ██║╚══██╔══╝
  ███╔╝ █████╗  ██████╔╝██║   ██║    ██████╔╝██║   ██║██║██╔██╗ ██║   ██║
 ███╔╝  ██╔══╝  ██╔══██╗██║   ██║    ██╔═══╝ ██║   ██║██║██║╚██╗██║   ██║
███████╗███████╗██║  ██║╚██████╔╝    ██║     ╚██████╔╝██║██║ ╚████║   ██║
╚══════╝╚══════╝╚═╝  ╚═╝ ╚═════╝     ╚═╝      ╚═════╝ ╚═╝╚═╝  ╚═══╝   ╚═╝
██████╗  ██████╗ ███╗   ███╗ █████╗ ██╗███╗   ██╗███████╗
██╔══██╗██╔═══██╗████╗ ████║██╔══██╗██║████╗  ██║██╔════╝
██║  ██║██║   ██║██╔████╔██║███████║██║██╔██╗ ██║███████╗
██║  ██║██║   ██║██║╚██╔╝██║██╔══██║██║██║╚██╗██║╚════██║
██████╔╝╚██████╔╝██║ ╚═╝ ██║██║  ██║██║██║ ╚████║███████║
╚═════╝  ╚═════╝ ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝╚══════╝`;

const bootMessages = [
  "ZERO POINT DOMAINS (C) 2025",
  "INITIALIZING MEMORY............ OK",
  "CHECKING DISK DRIVE A:......... OK",
  "LOADING KERNEL................ OK",
  "MOUNTING FILE SYSTEM........... OK",
  "STARTING NETWORK DAEMON........ OK",
  "LOADING AUTH MODULE............ OK",
  "",
  "SYSTEM READY.",
  "",
];

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  showBootSequence = false,
}) => {
  const [showCursor, setShowCursor] = useState(true);
  const [bootComplete, setBootComplete] = useState(!showBootSequence);
  const [bootLines, setBootLines] = useState<string[]>([]);

  // Boot sequence effect
  useEffect(() => {
    if (showBootSequence && !bootComplete) {
      let lineIndex = 0;
      const interval = setInterval(() => {
        if (lineIndex < bootMessages.length) {
          setBootLines((prev) => [...prev, bootMessages[lineIndex]]);
          lineIndex++;
        } else {
          clearInterval(interval);
          setTimeout(() => setBootComplete(true), 500);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [showBootSequence, bootComplete]);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  if (!bootComplete) {
    return (
      <div
        className="min-h-screen bg-black font-mono flex flex-col p-8 overflow-hidden"
        style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.8)" }}
      >
        {/* Scanline overlay */}
        <div
          className="fixed inset-0 pointer-events-none z-50"
          style={{
            background:
              "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 2px)",
          }}
        />

        <div className="text-green-500 text-sm leading-relaxed">
          {bootLines.map((line, i) => (
            <div key={i} className="whitespace-pre">
              {line}
              {i === bootLines.length - 1 && (
                <span className={showCursor ? "opacity-100" : "opacity-0"}>
                  █
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black font-mono flex flex-col items-center justify-center p-4 overflow-hidden"
      style={{ textShadow: "0 0 10px rgba(34, 197, 94, 0.8)" }}
    >
      {/* Scanline overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-50"
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 2px)",
        }}
      />

      {/* CRT flicker effect */}
      <div
        className="fixed inset-0 pointer-events-none z-40 opacity-[0.03]"
        style={{
          background:
            "linear-gradient(rgba(34, 197, 94, 0.1) 50%, transparent 50%)",
          backgroundSize: "100% 4px",
          animation: "flicker 0.15s infinite",
        }}
      />

      {/* CRT vignette */}
      <div
        className="fixed inset-0 pointer-events-none z-30"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%)",
        }}
      />

      {/* Main container */}
      <div
        className="w-full max-w-2xl border-2 border-green-500 bg-black p-8 relative z-10"
        style={{
          boxShadow:
            "0 0 30px rgba(34, 197, 94, 0.3), inset 0 0 60px rgba(34, 197, 94, 0.05)",
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <pre className="text-green-500 text-[6px] sm:text-[8px] md:text-[10px] mb-4 leading-none whitespace-pre tracking-wide">
            {ASCII_LOGO}
          </pre>
          <div className="text-green-400 text-lg tracking-widest mb-1">
            DOMAIN ACQUISITION SYSTEM
          </div>
          {title && (
            <div className="text-green-600 text-sm tracking-wider mt-2">
              {title}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-green-700 mb-6" />

        {/* Content */}
        {children}

        {/* Footer */}
        <div className="mt-8 text-center space-y-2">
          <div className="text-green-800 text-xs">
            ════════════════════════════════════
          </div>
          <div className="text-green-700 text-xs tracking-wider">
            THE ZERO ENERGY GROUP
          </div>
          <div className="text-green-800 text-xs">2025</div>
        </div>
      </div>

      {/* Bottom status bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t-2 border-green-700 bg-black px-4 py-2 flex justify-between text-xs text-green-700 z-10">
        <span>MEM: 64K OK</span>
        <span className="animate-pulse">AWAITING INPUT...</span>
        <span>
          {new Date().toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </span>
      </div>

      <style>{`
        @keyframes flicker {
          0% { opacity: 0.03; }
          50% { opacity: 0.05; }
          100% { opacity: 0.03; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
};

export default AuthLayout;
